import { useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Alert, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import { useAuth } from "@/auth/AuthContext";
import { ApiError, get } from "@/api/client";
import AuthLayout from "@/brand/AuthLayout";
import { KeyIcon } from "@/design/icons/KeyIcon";

interface OidcConfig {
  enabled: boolean;
  provider_name: string | null;
  login_url: string | null;
}

/** What /api/auth/oidc/callback's `sso_error` codes mean to the person signing in. */
const SSO_ERRORS: Record<string, string> = {
  state: "The single sign-on attempt expired or was interrupted. Try again.",
  idp_error: "Single sign-on was cancelled or refused by the identity provider.",
  idp_unavailable: "The identity provider could not be reached. Try again, or sign in with your password.",
  token_rejected: "The identity provider did not complete the sign-in. Try again.",
  invalid_token: "The identity provider's answer could not be verified. Try again.",
  no_email: "Your SSO account has no email address.",
  unverified_email: "Your SSO email address is not verified.",
  no_account: "There is no Console account for your SSO email address. Ask an administrator to add you.",
  no_role: "None of your SSO groups gives access to the Console.",
  disabled: "Your Console account is disabled.",
  not_active: "Your Console account has not been activated yet.",
  service_account: "This account signs in with API keys only.",
  org_suspended: "Your organisation is suspended.",
  not_staff: "Single sign-on is for MultiDoc staff. Sign in with your email and password.",
  conflict: "This Console account is linked to a different SSO identity.",
};

export default function LoginPage() {
  const { login, verifyMfa, mfaPending, resumeMfa } = useAuth();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const ssoError = params.get("sso_error");
  const [error, setError] = useState<string | null>(
    ssoError ? (SSO_ERRORS[ssoError] ?? "Single sign-on failed. Try again, or sign in with your password.") : null,
  );
  const [busy, setBusy] = useState(false);
  const [sso, setSso] = useState<OidcConfig | null>(null);

  useEffect(() => {
    // Back from SSO with a session that still owes the authenticator code.
    if (params.get("sso") === "mfa") resumeMfa();
  }, [params, resumeMfa]);

  useEffect(() => {
    let live = true;
    get<OidcConfig>("/auth/oidc/config")
      .then((c) => live && setSso(c.enabled && c.login_url ? c : null))
      .catch(() => live && setSso(null));
    return () => {
      live = false;
    };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mfaPending) await verifyMfa(code);
      else await login(email, password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <Stack component="form" onSubmit={submit} spacing={2.5}>
        <div>
          <Typography variant="h5" fontWeight={600}>
            {mfaPending ? "Two-factor code" : "Sign in"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {mfaPending
              ? "Enter the six-digit code from your authenticator."
              : "Use your MultiDoc or partner account."}
          </Typography>
        </div>

        {error && <Alert severity="error">{error}</Alert>}

        {mfaPending ? (
          <TextField
            label="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
            slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 12 } }}
          />
        ) : (
          <>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              autoComplete="username"
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </>
        )}

        <Button type="submit" variant="contained" size="large" disabled={busy}>
          {busy ? "Signing in…" : mfaPending ? "Verify" : "Sign in"}
        </Button>
        {!mfaPending && sso ? (
          <>
            <Divider>
              <Typography variant="caption" color="text.secondary">
                or
              </Typography>
            </Divider>
            <Button
              variant="outlined"
              size="large"
              startIcon={<KeyIcon />}
              disabled={busy}
              // A full navigation, not fetch: the IdP's sign-in page takes over the tab.
              href={sso.login_url ?? undefined}
            >
              Sign in with {sso.provider_name}
            </Button>
          </>
        ) : null}
        {!mfaPending ? (
          <Typography variant="caption" color="text.secondary">
            Reseller without an account? <RouterLink to="/register">Become a partner</RouterLink>
          </Typography>
        ) : null}
      </Stack>
    </AuthLayout>
  );
}
